import React, { useEffect, useState } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Music", href: "#music" },
  { label: "Tour", href: "#tour" },
  { label: "Merch", href: "#merch" },
  { label: "Story", href: "#bio" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { dispatch, count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-ink-950/70 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span
            className="w-9 h-9 flex items-center justify-center group-hover:scale-110 transition-transform"
            style={{ filter: "drop-shadow(0 0 10px rgba(212,175,55,0.55))" }}
            aria-label="Eye of Horus"
          >
            <svg viewBox="0 0 64 64" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="horus-gold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FDE047" />
                  <stop offset="45%" stopColor="#D4AF37" />
                  <stop offset="100%" stopColor="#A8821C" />
                </linearGradient>
              </defs>
              <g
                fill="none"
                stroke="url(#horus-gold)"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {/* Upper eyelid arc */}
                <path d="M6 30 C 18 18, 38 18, 54 28" />
                {/* Lower eyelid arc */}
                <path d="M6 30 C 18 40, 38 40, 54 28" />
                {/* Eyebrow */}
                <path d="M10 22 C 22 14, 40 14, 56 22" strokeWidth="2.2" />
                {/* Vertical teardrop / line under pupil */}
                <path d="M28 36 L 24 50" strokeWidth="2.2" />
                {/* Curving tail (spiral hook) right side */}
                <path d="M40 36 C 48 40, 56 44, 58 54 C 50 52, 44 50, 40 46" strokeWidth="2.2" />
              </g>
              {/* Pupil */}
              <circle cx="30" cy="30" r="4.2" fill="url(#horus-gold)" />
            </svg>
          </span>
          <span className="font-display font-black tracking-[0.3em] text-sm md:text-base">
            CASSIUS<span className="text-gold">·</span>GRATE
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.25em] text-slate-300 hover:text-cyan-glow transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="cart-toggle"
            onClick={() => dispatch({ type: "toggle" })}
            className="relative flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-cyan-glow hover:text-cyan-glow transition-all rounded-sm group"
            aria-label="Open cart"
          >
            <ShoppingBag size={16} />
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em]">Cart</span>
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-gold text-ink-950 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((v) => !v)}
            className="md:hidden p-2"
            aria-label="Menu"
          >
            {mobile ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden border-t border-white/5 bg-ink-950/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobile(false)}
                className="text-sm uppercase tracking-[0.25em] text-slate-300 hover:text-cyan-glow"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
